import {
  getMessaging,
  getToken,
  onMessage,
  isSupported,
  type Messaging,
} from 'firebase/messaging'
import app from '@/lib/firebase'
import {
  orderNotificationOptions,
  playOrderAlertSound,
} from '@/lib/orderAlertSound'
import { LOGO_ICON_PATH, FAVICON_32_PATH } from '@/lib/brand'

let messagingInstance: Messaging | null = null
let serviceWorkerRegistration: ServiceWorkerRegistration | null = null

export async function getMessagingIfSupported(): Promise<Messaging | null> {
  if (typeof window === 'undefined') return null
  const supported = await isSupported()
  if (!supported) return null
  if (!messagingInstance) {
    messagingInstance = getMessaging(app)
  }
  return messagingInstance
}

export function isPushConfigured(): boolean {
  return Boolean(import.meta.env.VITE_FIREBASE_VAPID_KEY)
}

/** Web Notifications API — limited / absent on many mobile browsers (incl. iOS). */
export function isNotificationSupported(): boolean {
  return typeof window !== 'undefined' && typeof Notification !== 'undefined'
}

export function getNotificationPermission(): NotificationPermission | null {
  if (!isNotificationSupported()) return null
  return Notification.permission
}

export async function registerMessagingServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null
  try {
    const existing = await navigator.serviceWorker.getRegistration('/')
    if (existing?.active?.scriptURL?.includes('firebase-messaging-sw')) {
      serviceWorkerRegistration = existing
      return existing
    }
    serviceWorkerRegistration = await navigator.serviceWorker.register(
      '/firebase-messaging-sw.js',
      { scope: '/', updateViaCache: 'none' }
    )
    await serviceWorkerRegistration.update()
    return serviceWorkerRegistration
  } catch (err) {
    console.warn('Service worker registration failed', err)
    return null
  }
}

export async function getActiveServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (serviceWorkerRegistration) return serviceWorkerRegistration
  if (!('serviceWorker' in navigator)) return null
  return navigator.serviceWorker.ready
}

/** System notification (works in foreground when tab is open). */
export async function showBrowserNotification(
  title: string,
  body: string,
  data?: { orderId?: string; link?: string }
): Promise<void> {
  if (
    typeof window === 'undefined' ||
    !isNotificationSupported() ||
    Notification.permission !== 'granted'
  ) {
    return
  }
  playOrderAlertSound()

  try {
    const reg = await getActiveServiceWorker()
    const soundOpts = orderNotificationOptions()
    if (reg) {
      await reg.showNotification(title, {
        body,
        icon: LOGO_ICON_PATH,
        badge: FAVICON_32_PATH,
        data: { ...data },
        tag: data?.orderId ? `order-${data.orderId}` : 'ooud-kraft-admin-order',
        ...soundOpts,
      } as NotificationOptions)
    } else {
      new Notification(title, {
        body,
        icon: LOGO_ICON_PATH,
        ...soundOpts,
      } as NotificationOptions)
    }
  } catch (err) {
    console.warn('showBrowserNotification failed', err)
  }
}

export async function requestAdminFcmToken(): Promise<string | null> {
  if (!isPushConfigured()) {
    console.warn('VITE_FIREBASE_VAPID_KEY is not set')
    return null
  }

  const messaging = await getMessagingIfSupported()
  if (!messaging) {
    console.warn('FCM not supported in this browser')
    return null
  }

  if (!isNotificationSupported()) {
    console.warn('Notifications not supported in this browser')
    return null
  }

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return null

  const registration = await registerMessagingServiceWorker()
  if (!registration) return null
  await navigator.serviceWorker.ready
  // Pick up SW changes (Android Chrome caches aggressively).
  await registration.update().catch(() => {})

  const token = await getToken(messaging, {
    vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
    serviceWorkerRegistration: registration,
  })

  return token || null
}

export async function subscribeForegroundMessages(
  handler: (payload: {
    title?: string
    body?: string
    orderId?: string
    link?: string
  }) => void
): Promise<(() => void) | null> {
  const messaging = await getMessagingIfSupported()
  if (!messaging) return null

  return onMessage(messaging, async (payload) => {
    const title =
      payload.notification?.title ||
      payload.data?.title ||
      'New order placed'
    const body = payload.notification?.body || payload.data?.body || ''
    const orderId = payload.data?.orderId
    const link = payload.data?.link

    await showBrowserNotification(title, body, { orderId, link })

    handler({ title, body, orderId, link })
  })
}
